// @ts-nocheck
import React, { useState, useEffect, useMemo, useRef } from 'react';
import authService from './authService';
import { formatDateEU } from './CleaningDashboard'; // ISO yyyy-mm-dd → ελληνικό dd/mm/yyyy (μόνο εμφάνιση)

// Τραπεζικές Πληρωμές — μόνιμο αρχείο κινήσεων.
// Upload Πειραιώς PDF → categorize → confirm each match to booking/invoice/company expense.
// ΟΛΕΣ οι κινήσεις μένουν ορατές (pending + confirmed + ignored) με badge κατάστασης.
// Search server-side (ILIKE) + φίλτρα μήνα/ημέρας. Anti-διπλή καταχώρηση με override.
// Role gate is enforced SERVER-SIDE (isAdmin||isFinancial); UI mirrors it.

const BankReconciliation: React.FC = () => {
  const user = authService.getCurrentUser();
  const code = user?.code || '';

  const [txns, setTxns] = useState<any[]>([]);
  const [periods, setPeriods] = useState<string[]>([]);
  const [vessels, setVessels] = useState<any[]>([]);
  const [bookings, setBookings] = useState<any[]>([]);
  const [period, setPeriod] = useState<string>('');
  const [from, setFrom] = useState<string>('');
  const [to, setTo] = useState<string>('');
  const [q, setQ] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [msg, setMsg] = useState<string>('');
  /* Γραμμές που το μοντέλο επέστρεψε αλλά ΔΕΝ βρέθηκαν στο ίδιο το PDF.
     ΔΕΝ έχουν μπει στη βάση — ο χρήστης τις κρίνει μία-μία. */
  const [unverified, setUnverified] = useState<any[]>([]);
  const [unvPick, setUnvPick] = useState<Record<number, boolean>>({});
  const [coverage, setCoverage] = useState<string>('');
  // per-row manual selections
  const [pick, setPick] = useState<Record<number, string>>({}); // txnId -> booking_number | vessel_id
  // override warning modal: holds the pending action to re-send with force=true
  const [warn, setWarn] = useState<{ qs: string; body: any; okMsg: string; message: string } | null>(null);

  const api = (qs: string, opts: any = {}) =>
    fetch('/api/bank-reconciliation.php' + qs, {
      method: opts.method || 'GET',
      headers: { 'Content-Type': 'application/json', 'X-Employee-Code': code },
      ...(opts.body ? { body: JSON.stringify(opts.body) } : {}),
    });

  const load = async () => {
    setLoading(true);
    try {
      const qs = '?action=list'
        + (period ? '&period=' + encodeURIComponent(period) : '')
        + (from ? '&from=' + encodeURIComponent(from) : '')
        + (to ? '&to=' + encodeURIComponent(to) : '')
        + (q.trim() ? '&q=' + encodeURIComponent(q.trim()) : '');
      const r = await api(qs);
      const j = await r.json();
      if (!r.ok) { setMsg(j.error || 'Σφάλμα φόρτωσης'); return; }
      setTxns(j.transactions || []);
      setPeriods(j.periods || []);
      setVessels(j.vessels || []);
      setBookings(j.bookings || []);
    } catch (e: any) { setMsg('Σφάλμα δικτύου'); }
    finally { setLoading(false); }
  };

  // Debounce reloads on filter/search changes (search is server-side ILIKE).
  const timer = useRef<any>(null);
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(load, 300);
    return () => timer.current && clearTimeout(timer.current);
    /* eslint-disable-next-line */
  }, [period, from, to, q]);

  const onUpload = async (file: File) => {
    setImporting(true); setMsg('');
    try {
      const b64: string = await new Promise((res, rej) => {
        const fr = new FileReader();
        fr.onload = () => res(String(fr.result));
        fr.onerror = rej;
        fr.readAsDataURL(file);
      });
      const r = await api('?action=import', { method: 'POST', body: { pdf_base64: b64 } });
      const j = await r.json();
      if (!r.ok) { setMsg('❌ ' + (j.error || 'Import απέτυχε')); return; }
      setUnverified(Array.isArray(j.unverified) ? j.unverified : []);
      setUnvPick({});
      setCoverage(j.coverage_warning || '');
      const ver = j.verification === 'unavailable'
        ? ' ⚠️ ΧΩΡΙΣ ΕΠΑΛΗΘΕΥΣΗ (σκαναρισμένο PDF, δεν βγαίνει κείμενο).'
        : (j.unverified_count > 0 ? ` ⚠️ ${j.unverified_count} ΑΝΕΠΑΛΗΘΕΥΤΕΣ — δεν μπήκαν, δες παρακάτω.` : '');
      setMsg(`✅ Import: ${j.new} νέες, ${j.duplicates} διπλότυπες, ${j.skipped_fees} προμήθειες skip, ${j.skipped_internal} internal skip (από ${j.total_rows} γραμμές).${ver}`);
      await load();
    } catch (e: any) { setMsg('❌ Σφάλμα import'); }
    finally { setImporting(false); }
  };

  /* Ο χρήστης ενέκρινε ρητά κάποιες ανεπαλήθευτες: πάνε από τον ΙΔΙΟ
     categorize/dedup δρόμο, απλώς χωρίς τον έλεγχο απέναντι στο PDF. */
  const approveUnverified = async () => {
    const rows = unverified.filter((_, i) => unvPick[i]).map((u) => u.raw);
    if (!rows.length) { setMsg('Δεν επέλεξες καμία γραμμή.'); return; }
    const r = await api('?action=import-approved', { method: 'POST', body: { rows } });
    const j = await r.json();
    if (!r.ok) { setMsg('❌ ' + (j.error || 'Σφάλμα')); return; }
    setMsg(`✅ Καταχωρήθηκαν ${j.new} από τις ${j.approved} που ενέκρινες.`);
    setUnverified(unverified.filter((_, i) => !unvPick[i]));
    setUnvPick({});
    await load();
  };

  // proposed booking for a payment row: first booking whose digits == booking_guess
  const bookingForGuess = (guess: string) =>
    guess ? bookings.find((b) => b.digits === String(guess)) : undefined;
  const vesselIdForGuess = (vguess: string) => {
    if (!vguess) return '';
    const v = vessels.find((x) => (x.name || '').toUpperCase() === String(vguess).toUpperCase());
    return v ? String(v.id) : '';
  };
  const vesselName = (id: any) => vessels.find((v) => String(v.id) === String(id))?.name || '';

  const selectedFor = (t: any) => {
    if (pick[t.id] !== undefined) return pick[t.id];
    if (t.matched_booking_number) return t.matched_booking_number;   // already confirmed payment
    if (t.matched_vessel_id) return String(t.matched_vessel_id);     // already confirmed vessel
    if (t.category === 'payment') {
      // προ-επιλογή: #1 πρόταση (server ranking) → αλλιώς το booking_guess.
      if (t.suggestions && t.suggestions.length) return t.suggestions[0].booking_number;
      return bookingForGuess(t.booking_guess)?.booking_number || '';
    }
    if (t.category === 'vessel_expense') return vesselIdForGuess(t.vessel_guess);
    return '';
  };

  // Central POST handler: on {warning} → open override modal instead of erroring.
  const act = async (qs: string, body: any, okMsg: string) => {
    const r = await api(qs, { method: 'POST', body });
    const j = await r.json();
    if (!r.ok) { setMsg('❌ ' + (j.error || 'Σφάλμα')); return; }
    if (j.warning) { setWarn({ qs, body, okMsg, message: j.message || 'Έχει ήδη καταχωρηθεί. Καταχώρηση ξανά;' }); return; }
    setMsg('✅ ' + okMsg);
    await load(); // rows stay visible with updated status badge
  };

  const forceProceed = async () => {
    if (!warn) return;
    const { qs, body, okMsg } = warn;
    setWarn(null);
    await act(qs, { ...body, force: true }, okMsg);
  };

  const confirmPayment = (t: any) => {
    const bn = selectedFor(t);
    if (!bn) { setMsg('Διάλεξε booking πρώτα.'); return; }
    act('?action=confirm-payment', { txn_id: t.id, booking_number: bn }, `Πληρωμή €${t.amount} → ${bn}`);
  };
  const confirmVessel = (t: any) => {
    const vid = selectedFor(t);
    if (!vid) { setMsg('Διάλεξε σκάφος πρώτα.'); return; }
    act('?action=confirm-vessel-expense', { txn_id: t.id, vessel_id: Number(vid) }, `Έξοδο σκάφους €${t.amount}`);
  };
  const confirmCompany = (t: any) =>
    act('?action=confirm-company-expense', { txn_id: t.id }, `Έξοδο εταιρείας €${t.amount}`);
  const ignore = (t: any) =>
    act('?action=ignore', { txn_id: t.id }, 'Αγνοήθηκε');

  // Διαγραφή — αφορά ΜΟΝΟ τον πίνακα bank_transactions (αρχείο τραπεζικών κινήσεων).
  // ΔΕΝ αγγίζει booking payments / invoices / company_expenses που έχεις ήδη καταχωρήσει.
  const deletePeriod = async () => {
    if (!period) { setMsg('Διάλεξε πρώτα μήνα (period) για διαγραφή.'); return; }
    if (!window.confirm(`Σίγουρα διαγραφή ΟΛΩΝ των τραπεζικών κινήσεων του ${period};\n\nΔεν αναιρείται. (Οι πληρωμές που έχεις ήδη καταχωρήσει σε bookings ΜΕΝΟΥΝ.)`)) return;
    const r = await api('?action=delete-period', { method: 'POST', body: { period } });
    const j = await r.json();
    if (!r.ok) { setMsg('❌ ' + (j.error || 'Σφάλμα διαγραφής')); return; }
    setMsg(`🗑 Διαγράφηκαν ${j.deleted} κινήσεις του ${period}.`);
    await load();
  };
  const deleteTxn = async (t: any) => {
    if (!window.confirm(`Διαγραφή αυτής της κίνησης (€${t.amount});\n\nΔεν αναιρείται. Αφορά μόνο το αρχείο τραπεζικών κινήσεων.`)) return;
    const r = await api('?action=delete-transaction', { method: 'POST', body: { txn_id: t.id } });
    const j = await r.json();
    if (!r.ok) { setMsg('❌ ' + (j.error || 'Σφάλμα διαγραφής')); return; }
    setMsg('🗑 Η κίνηση διαγράφηκε.');
    await load();
  };

  const money = (n: any) => Number(n).toLocaleString('el-GR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const catLabel: Record<string, string> = {
    pay_nomatch: '🔴 Πληρωμές ΧΩΡΙΣ αντιστοίχιση — χρειάζονται δουλειά',
    pay_matched: '🟡 Πληρωμές ματσαρισμένες — εκκρεμεί καταχώρηση',
    recorded: '✓ Καταχωρημένες — ήδη στο ναύλο',
    payment: '💰 Πληρωμές — Εκκρεμείς', vessel_expense: '⛵ Έξοδο σκάφους',
    company_expense: '🏢 Έξοδο εταιρείας', uncategorized: '❓ Αταξινόμητο',
  };

  // 🔥 ΝΕΑ ΣΕΙΡΑ (πληρωμές): α) ΧΩΡΙΣ αντιστοίχιση (καμία πρόταση) → β) ματσαρισμένες
  //    μη-καταχωρημένες (πρόταση, όχι confirmed) → γ) καταχωρημένες. Μέσα σε κάθε ομάδα
  //    value_date DESC (από τη σειρά του server SQL). recorded = already_in_booking Ή confirmed.
  const grouped = useMemo(() => {
    const isRec  = (t: any) => t.already_in_booking || t.status === 'confirmed';
    const hasSug = (t: any) => Array.isArray(t.suggestions) && t.suggestions.length > 0;
    const pays = txns.filter((t) => t.category === 'payment');
    return {
      pay_nomatch:     pays.filter((t) => !isRec(t) && !hasSug(t)),
      pay_matched:     pays.filter((t) => !isRec(t) &&  hasSug(t)),
      recorded:        pays.filter((t) => isRec(t)),
      vessel_expense:  txns.filter((t) => t.category === 'vessel_expense'),
      company_expense: txns.filter((t) => t.category === 'company_expense' || t.category === 'uncategorized'),
    };
  }, [txns]);

  // Status badge + matched target detail.
  const StatusBadge = (t: any) => {
    if (t.status === 'confirmed') {
      let detail = '';
      if (t.matched_booking_number) detail = ` → ${t.matched_booking_number}`;
      else if (t.matched_vessel_id) detail = ` → ${vesselName(t.matched_vessel_id)}`;
      return <span style={{ ...badge, background: '#e8f5e9', color: '#2e7d32' }}>✓ Καταχωρήθηκε{detail}</span>;
    }
    if (t.status === 'ignored') return <span style={{ ...badge, background: '#eeeeee', color: '#777' }}>Αγνοήθηκε</span>;
    // Ήδη στο ναύλο (already_in_booking) → κρύψε το «Εκκρεμεί» (αντιφατικό με το πράσινο «✓ ήδη στο…»).
    // Τα κουμπιά Καταχώρηση/Αγνόησε + η πράσινη ένδειξη + το group ΜΕΝΟΥΝ ως έχουν.
    if (t.already_in_booking) return null;
    return <span style={{ ...badge, background: '#fff8e1', color: '#b26a00' }}>Εκκρεμεί</span>;
  };

  // Render the action controls for a row depending on category + status.
  const controls = (t: any) => {
    const confirmed = t.status === 'confirmed';
    if (t.category === 'payment') {
      return (
        <>
          {StatusBadge(t)}
          <select value={selectedFor(t)} onChange={(e) => setPick((p) => ({ ...p, [t.id]: e.target.value }))} style={sel}>
            <option value="">— διάλεξε booking —</option>
            {t.suggestions && t.suggestions.length > 0 && (
              <optgroup label="⭐ Προτάσεις (πιθανά ναύλα)">
                {t.suggestions.map((s: any) => (
                  <option key={'sug-' + s.booking_number} value={s.booking_number}>
                    ⭐ {s.booking_number}{s.vessel ? ` · ${s.vessel}` : ''} — {s.score}%{s.hits?.length ? ` (${s.hits.join(', ')})` : ''}
                  </option>
                ))}
              </optgroup>
            )}
            <optgroup label="Όλα τα ναύλα">
              {bookings.map((b) => (
                <option key={b.booking_number} value={b.booking_number}>
                  {b.booking_number}{b.vessel ? ` · ${b.vessel}` : ''}
                </option>
              ))}
            </optgroup>
          </select>
          {/* Ήδη στο ναύλο → ΕΝΙΑΙΟ κουμπί «↻ Καταχώρηση ξανά» (override) + Αγνόησε,
              ανεξάρτητα αν το bank status είναι pending ή confirmed. Οι πραγματικές
              Εκκρεμείς (already_in_booking=false) κρατούν το σκέτο «Καταχώρηση». */}
          {t.already_in_booking
            ? <>
                <button onClick={() => confirmPayment(t)} style={btnOverride}>↻ Καταχώρηση ξανά</button>
                <button onClick={() => ignore(t)} style={btnGray}>Αγνόησε</button>
              </>
            : (confirmed
                ? <button onClick={() => confirmPayment(t)} style={btnOverride}>↻ Καταχώρηση ξανά</button>
                : <>
                    <button onClick={() => confirmPayment(t)} style={btnGreen}>✓ Καταχώρηση πληρωμής</button>
                    <button onClick={() => ignore(t)} style={btnGray}>Αγνόησε</button>
                  </>)}
        </>
      );
    }
    if (t.category === 'vessel_expense') {
      return (
        <>
          {StatusBadge(t)}
          {!t.vessel_guess && !confirmed && <span style={{ color: '#c62828', fontSize: 12 }}>⚠ αμφίβολο σκάφος</span>}
          <select value={selectedFor(t)} onChange={(e) => setPick((p) => ({ ...p, [t.id]: e.target.value }))} style={sel}>
            <option value="">— διάλεξε σκάφος —</option>
            {vessels.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
          {confirmed
            ? <button onClick={() => confirmVessel(t)} style={btnOverride}>↻ Καταχώρηση ξανά</button>
            : <>
                <button onClick={() => confirmVessel(t)} style={btnGreen}>✓ Καταχώρηση εξόδου</button>
                <button onClick={() => ignore(t)} style={btnGray}>Αγνόησε</button>
              </>}
        </>
      );
    }
    // company_expense / uncategorized
    return (
      <>
        {StatusBadge(t)}
        {confirmed
          ? <button onClick={() => confirmCompany(t)} style={btnOverride}>↻ Καταχώρηση ξανά</button>
          : <>
              <button onClick={() => confirmCompany(t)} style={btnGreen}>✓ Έξοδο εταιρείας</button>
              <button onClick={() => ignore(t)} style={btnGray}>Αγνόησε</button>
            </>}
      </>
    );
  };

  const renderSection = (key: string) => grouped[key].length > 0 && (
    <Section title={`${catLabel[key]} (${grouped[key].length})`}>
      {grouped[key].map((t: any) => (
        <Row key={t.id} t={t} money={money}>
          {controls(t)}
          <button onClick={() => deleteTxn(t)} title="Διαγραφή κίνησης (μόνο από το αρχείο τραπεζικών κινήσεων)" style={btnTrash}>🗑</button>
        </Row>
      ))}
    </Section>
  );

  // «✓ Καταχωρημένες»: κίνηση που βρέθηκε ΗΔΗ σε payment ναύλου (authoritative). Πράσινη
  // ένδειξη σε ποιο ναύλο + πώς βρέθηκε. Τα controls μένουν (override αν χρειαστεί).
  const renderRecorded = () => grouped.recorded.length > 0 && (
    <Section title={`${catLabel.recorded} (${grouped.recorded.length})`}>
      {grouped.recorded.map((t: any) => (
        <Row key={t.id} t={t} money={money}>
          <span style={{ ...badge, background: '#e8f5e9', color: '#2e7d32' }}
                title={t.match_hits?.length ? 'Βρέθηκε με: ' + t.match_hits.join(', ') : ''}>
            ✓ ήδη στο {t.matched_booking_auto}{t.match_hits?.length ? ` · ${t.match_hits.join(', ')}` : ''}
          </span>
          {controls(t)}
          <button onClick={() => deleteTxn(t)} title="Διαγραφή κίνησης (μόνο από το αρχείο τραπεζικών κινήσεων)" style={btnTrash}>🗑</button>
        </Row>
      ))}
    </Section>
  );

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>Τραπεζικές Πληρωμές</h2>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        <label style={{ padding: '8px 14px', background: importing ? '#9e9e9e' : '#1976d2', color: '#fff', borderRadius: 6, cursor: importing ? 'default' : 'pointer' }}>
          {importing ? '⏳ Ανάλυση PDF…' : '📤 Ανέβασμα κατάστασης (PDF)'}
          <input type="file" accept="application/pdf" disabled={importing} style={{ display: 'none' }}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); e.currentTarget.value = ''; }} />
        </label>

        <span style={{ marginLeft: 8 }}>Μήνας:</span>
        <select value={period} onChange={(e) => setPeriod(e.target.value)} style={{ padding: '6px 10px', borderRadius: 6 }}>
          <option value="">Όλοι</option>
          {periods.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>

        <span>Από:</span>
        <input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid #ccc' }} />
        <span>Μέχρι:</span>
        <input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid #ccc' }} />
        {(from || to) && <button onClick={() => { setFrom(''); setTo(''); }} style={{ ...btnGray, padding: '4px 8px' }}>✕ Καθαρισμός ημ/νιών</button>}

        <button onClick={load} style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #ccc', background: '#fff', cursor: 'pointer' }}>↻ Ανανέωση</button>

        {period && (
          <button onClick={deletePeriod} title="Διαγραφή όλων των κινήσεων του επιλεγμένου μήνα"
            style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #ef9a9a', background: '#ffebee', color: '#c62828', cursor: 'pointer', fontWeight: 700 }}>
            🗑 Διαγραφή μήνα {period}
          </button>
        )}
      </div>

      {/* SEARCH */}
      <div style={{ marginBottom: 14 }}>
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="🔎 Αναζήτηση: πελάτης, broker, σκάφος, booking, ποσό, ημερομηνία…"
          style={{ width: '100%', maxWidth: 520, padding: '8px 12px', borderRadius: 6, border: '1px solid #ccc', fontSize: 14 }}
        />
        {q && <button onClick={() => setQ('')} style={{ ...btnGray, marginLeft: 8, padding: '6px 10px' }}>Καθαρισμός</button>}
      </div>

      {msg && <div style={{ marginBottom: 14, padding: 10, background: '#f0f4ff', borderRadius: 6, fontSize: 14 }}>{msg}</div>}

      {/* ΕΛΕΓΧΟΣ ΚΑΛΥΨΗΣ: το PDF δείχνει περισσότερες γραμμές απ' όσες βρήκε το
          μοντέλο. Δεν μπλοκάρει τίποτα — είναι εκτίμηση, όχι απόδειξη. */}
      {coverage && (
        <div style={{ marginBottom: 14, padding: 10, background: '#fff4e5', border: '1px solid #f0a', borderColor: '#e8a33d', borderRadius: 6, fontSize: 14 }}>
          ⚠️ {coverage}
        </div>
      )}

      {/* ΑΝΕΠΑΛΗΘΕΥΤΕΣ: γυρίστηκαν από το μοντέλο αλλά ΔΕΝ βρέθηκαν στο PDF.
          ΔΕΝ έχουν μπει στη βάση. Μπαίνουν μόνο αν τις εγκρίνει ρητά ο χρήστης. */}
      {unverified.length > 0 && (
        <div style={{ marginBottom: 14, padding: 12, background: '#fff0f0', border: '2px solid #d33', borderRadius: 6 }}>
          <div style={{ fontWeight: 700, marginBottom: 6, color: '#a00' }}>
            {unverified.length} γραμμές ΔΕΝ επαληθεύτηκαν στο PDF — δεν καταχωρήθηκαν
          </div>
          <div style={{ fontSize: 13, color: '#666', marginBottom: 10 }}>
            Το ποσό και η ημερομηνία δεν βρέθηκαν μαζί σε καμία γραμμή του αρχείου.
            Τσέκαρε όποια αναγνωρίζεις ως σωστή και καταχώρησέ την χειροκίνητα.
          </div>
          <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid #ccc' }}>
                <th style={{ width: 30 }}></th><th>Ημ/νία</th><th>Ποσό</th><th>Κατ.</th><th>Περιγραφή</th>
              </tr>
            </thead>
            <tbody>
              {unverified.map((u, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                  <td><input type="checkbox" checked={!!unvPick[i]}
                             onChange={(e) => setUnvPick({ ...unvPick, [i]: e.target.checked })} /></td>
                  <td>{u.value_date || '—'}</td>
                  <td style={{ fontWeight: 600 }}>{u.amount} €</td>
                  <td>{u.direction === 'in' ? 'είσπραξη' : 'πληρωμή'}</td>
                  <td>{u.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <button onClick={approveUnverified}
                  style={{ marginTop: 10, padding: '7px 14px', background: '#d33', color: '#fff', border: 0, borderRadius: 5, cursor: 'pointer' }}>
            Καταχώρηση των επιλεγμένων παρά την αποτυχία επαλήθευσης
          </button>
        </div>
      )}
      {loading && <p>Φόρτωση…</p>}
      {!loading && txns.length === 0 && <p style={{ color: '#888' }}>Καμία κίνηση. Ανέβασε κατάσταση Πειραιώς ή άλλαξε φίλτρα/αναζήτηση.</p>}
      {loading && <p>Φόρτωση…</p>}
      {!loading && txns.length === 0 && <p style={{ color: '#888' }}>Καμία κίνηση. Ανέβασε κατάσταση Πειραιώς ή άλλαξε φίλτρα/αναζήτηση.</p>}

      {renderSection('pay_nomatch')}
      {renderSection('pay_matched')}
      {renderRecorded()}
      {renderSection('vessel_expense')}
      {renderSection('company_expense')}

      {/* OVERRIDE WARNING MODAL */}
      {warn && (
        <div style={overlay} onClick={() => setWarn(null)}>
          <div style={modal} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: 15, marginBottom: 18, lineHeight: 1.5 }}>⚠️ {warn.message}</div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setWarn(null)} style={btnGray}>Ακύρωση</button>
              <button onClick={forceProceed} style={btnGreen}>Καταχώρηση ούτως ή άλλως</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const sel: any = { padding: '5px 8px', borderRadius: 5, minWidth: 220 };
const btnGreen: any = { padding: '6px 12px', background: '#2e7d32', color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer' };
const btnGray: any = { padding: '6px 12px', background: '#eee', color: '#333', border: 'none', borderRadius: 5, cursor: 'pointer' };
const btnOverride: any = { padding: '6px 12px', background: '#fff', color: '#b26a00', border: '1px solid #e0a83e', borderRadius: 5, cursor: 'pointer', fontSize: 13 };
const btnTrash: any = { padding: '5px 9px', background: '#fff', color: '#c62828', border: '1px solid #ef9a9a', borderRadius: 5, cursor: 'pointer', fontSize: 13 };
const badge: any = { padding: '3px 9px', borderRadius: 12, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' };
const overlay: any = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 };
const modal: any = { background: '#fff', borderRadius: 10, padding: 22, maxWidth: 420, width: '90%', boxShadow: '0 8px 30px rgba(0,0,0,0.25)' };

const Section: React.FC<{ title: string }> = ({ title, children }) => (
  <div style={{ marginBottom: 22 }}>
    <h3 style={{ fontSize: 16, borderBottom: '1px solid #e0e0e0', paddingBottom: 6 }}>{title}</h3>
    {children}
  </div>
);

const Row: React.FC<{ t: any; money: (n: any) => string }> = ({ t, money, children }) => (
  <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', padding: '8px 10px', borderBottom: '1px solid #f0f0f0', opacity: t.status === 'ignored' ? 0.6 : 1 }}>
    <span style={{ fontFamily: 'monospace', color: '#555', minWidth: 88 }}>{formatDateEU(t.value_date)}</span>
    <span style={{ fontWeight: 700, minWidth: 100, color: t.direction === 'in' ? '#2e7d32' : '#c62828' }}>
      {t.direction === 'in' ? '+' : '−'} €{money(t.amount)}
    </span>
    <span style={{ flex: '1 1 320px', fontSize: 13 }}>{t.description_raw}</span>
    {children}
  </div>
);

export default BankReconciliation;
